/**
 * Which permission each Settings section requires.
 *
 * WHY THIS FILE EXISTS
 *
 * The Settings tab used to be gated on a single permission, `users.manage`, and
 * the sections beneath it were mostly not gated at all — Appearance and Import
 * carried checks, the other six did not, because the tab gate was doing all the
 * work. That is a fine arrangement right up until someone needs one section and
 * not the others, and then it fails hard: the default General Manager role is
 * granted `records.import` and denied `users.manage`, so it held a permission it
 * could never exercise. The import wizard was unreachable for the exact role
 * that does the onboarding import.
 *
 * So the gate moved down here, per section, and the tab is now derived from the
 * sections rather than asserted next to them. `tabPermissions.js` reads this
 * file; nobody maintains a second copy. That module's header explains what
 * happened the last time two files each kept their own idea of a tab's
 * permission, and this is the same hazard one level down.
 *
 * WHY THIS IS NOT A NEW HOLE. The server is default-deny per route: app.js
 * refuses to boot if any route in an authenticated router lacks a
 * `requirePermission` or `anyAuthenticated` marker. Showing a section decides
 * what a user SEES, not what they can do — every write behind these screens is
 * checked again server-side. Widening the tab gate would be reckless without
 * that property; with it, this is a visibility fix.
 *
 * Values are any-of, matching `can(...perms)` in permissions.js. An empty list
 * means everyone.
 *
 * Kept dependency-free on purpose. tabPermissions.js imports it and is itself
 * imported by tourRegistry, which a dozen components pull in — a React or icon
 * import here would land in all of their chunks.
 */
/**
 * The three jobs behind the People page's tabs, and what each one asks for.
 *
 * This lives here, next to the section gates, because the Accounts SECTION gate
 * is the union of these: whatever opens a tab opens the section, and nothing
 * else does. Writing the two independently is how the section came to open for
 * a user with none of its tabs usable.
 *
 * `users` asks for users.manage because that is what `/getuseraccounts`
 * demands. It used to ask for nothing — the tab, its table and its "Add User"
 * button rendered for anyone who could open the section, so a user holding
 * roles.manage or settings.edit but not users.manage got a 403 error popup on
 * every single visit to Settings, and a button whose only possible outcome was
 * another 403.
 *
 * Declaration order is tab order, which is also landing order: a user opens on
 * the first tab they can see.
 */
export const ACCOUNTS_TABS = Object.freeze({
    users: Object.freeze(['users.manage']),
    roles: Object.freeze(['roles.manage']),
    //Which cost figures the dealership hides is a dealership setting, and also
    //a question about what a role may see — mirrors POST /costfields, which
    //accepts either.
    costs: Object.freeze(['settings.edit', 'roles.manage']),
});

/** Any-of, like canSeeSettingsSection. A tab with no permission is not a tab. */
export function canSeeAccountsTab(tab, can) {
    const perms = ACCOUNTS_TABS[tab] || [];
    return perms.length > 0 && can(...perms);
}

/** The tabs this user may open, in declaration order. Never empty when the
 *  Accounts section is visible — the section's gate is built from these. */
export function visibleAccountsTabs(can) {
    return Object.keys(ACCOUNTS_TABS).filter((tab) => canSeeAccountsTab(tab, can));
}

export const SETTINGS_SECTIONS = Object.freeze({
    //Company profile and theme are edit forms, not read-only views, so they ask
    //for the permission that describes editing settings.
    Company:       Object.freeze(['settings.edit']),
    Appearance:    Object.freeze(['settings.edit']),

    //DERIVED from ACCOUNTS_TABS above, never written here: holding any one of
    //its tabs is exactly the reason to open the section. Spelling the union out
    //by hand is how "the section opens" and "a tab inside it is usable" drift
    //apart, and every such gap is an error popup or a button that can only 403.
    Accounts:      Object.freeze([...new Set(Object.values(ACCOUNTS_TABS).flat())]),

    //Credit defaults are dealership settings.
    Sales:         Object.freeze(['settings.edit']),

    ImportData:    Object.freeze(['records.import']),

    //Billing spends money the dealership will be invoiced for, which the catalog
    //deliberately separates from settings.edit. settings.edit is accepted too so
    //that whoever configures the dealership can still see what it is paying.
    Subscription:  Object.freeze(['billing.purchase', 'settings.edit']),
    Credits:       Object.freeze(['billing.purchase', 'settings.edit']),

    //NO PERSONAL SECTIONS HERE. Notification preferences used to be one, and
    //being ungated they made this whole tab visible to everybody. They now live
    //in the profile menu beside Security & passkeys, which is where the other
    //per-user settings already were. Settings is company configuration; if a
    //personal section is ever added back, SETTINGS_TAB_PERMISSIONS below will
    //silently reopen the tab to the entire company, so put it in the profile
    //menu instead.
});

/**
 * The section to land on, for a user who may not hold settings.edit.
 *
 * SETTINGS_SECTIONS is declared in rail order, so "first key this user can see"
 * is also "topmost item in the rail" — that ordering is a contract, not an
 * accident, and reordering the object moves the landing page.
 *
 * Returns undefined for a user who can open no section at all. That is not
 * reachable through the UI — canSeeSettingsTab is false for them, so the tab is
 * not there to click — but it stopped being impossible when the ungated
 * Notifications section moved to the profile menu, so callers must handle it.
 */
export function firstVisibleSection(can) {
    return Object.keys(SETTINGS_SECTIONS).find((key) => canSeeSettingsSection(key, can));
}

/** The permissions that would open a section, any one of them. */
export function sectionPermissions(key) {
    return SETTINGS_SECTIONS[key] || [];
}

/**
 * @param {string} key    a SETTINGS_SECTIONS key
 * @param {Function} can  variadic any-of check from GlobalContext
 */
export function canSeeSettingsSection(key, can) {
    const perms = sectionPermissions(key);
    return perms.length === 0 || can(...perms);
}

/** Visible when any one section is. False for a user holding none of them. */
export function canSeeSettingsTab(can) {
    return Object.keys(SETTINGS_SECTIONS).some((key) => canSeeSettingsSection(key, can));
}

/**
 * The same rule as canSeeSettingsTab, expressed as an any-of list for
 * TAB_PERMISSIONS.
 *
 * The union of every section's permissions would be WRONG, and wrong in a way
 * that reads as correct: an ungated section means the tab opens for everyone, so
 * a union — which no salesperson satisfies — would hide the tab from precisely
 * the people one of its sections was written for. Collapsing to the
 * everyone-list keeps this in step with canSeeSettingsTab BY CONSTRUCTION rather
 * than by both being edited together, which is the whole point of deriving it.
 *
 * Today every section is gated, so this IS the union and the tab is admin-shaped
 * again — it collapsed to `[]` while notification preferences lived here, and
 * corrected itself when they moved out. That self-correction is the point: the
 * rule is computed from the sections, so it cannot be left behind by an edit to
 * them.
 */
const someSectionIsUngated = Object.values(SETTINGS_SECTIONS).some((perms) => perms.length === 0);

export const SETTINGS_TAB_PERMISSIONS = Object.freeze(
    someSectionIsUngated
        ? []
        : [...new Set(Object.values(SETTINGS_SECTIONS).flat())]
);
